import { useMemo } from "react";
import { PhotoListItem } from "@/components/records/photo-list-item/photo-list-item";
import { readMetadataByFieldName } from "@/components/common/image-viewer-modal/use-submit-record";
import { ItemCell, TombstoneRow } from "./collection-item-cell";
import type {
  ProjectMetadataField,
  ProjectPlan,
  ProjectRecord,
  ReportCollectionItem,
} from "@/types/api";

/** Name of the metadata field rendered as a row's description, as on Photos. */
const DESCRIPTION_FIELD = "remarks";

interface ReportPhotoListProps {
  items: ReportCollectionItem[];
  editing: boolean;
  onRemove: (itemId: string) => void;
  /** Project metadata fields — used to resolve each record's description. */
  metadataFields?: ProjectMetadataField[];
  /** Project plans — used to resolve a record's plan id to a name. */
  plans?: ProjectPlan[];
  onOpenRecord?: (record: ProjectRecord) => void;
  /** The record open in the side panel, highlighted in the list. */
  activeRecordId: string | null;
}

/**
 * A photos report in list view: the Photos page's rows, in the Photos page's
 * bordered block, with the report's rules around them — remove while editing,
 * a tombstone for a deleted photo, and no selection or swipe. The report pages
 * its own items, so this draws what it is handed rather than reusing the Photos
 * page's list and its record source. See [[report-detail-page.spec]] US-013.
 */
export function ReportPhotoList({
  items,
  editing,
  onRemove,
  metadataFields,
  plans,
  onOpenRecord,
  activeRecordId,
}: ReportPhotoListProps) {
  const planNameById = useMemo(
    () => new Map((plans ?? []).map((plan) => [plan.id, plan.name])),
    [plans],
  );

  return (
    <div className="divide-y divide-border overflow-hidden rounded-[10px] border border-border bg-card">
      {items.map((item) => (
        // Compact: the 24px control would crowd the row's chevron, as on the
        // issues table.
        <ItemCell
          key={item.id}
          item={item}
          editing={editing}
          onRemove={() => onRemove(item.id)}
          compact
        >
          {item.record && !item.is_deleted ? (
            <PhotoListItem
              record={item.record}
              description={readMetadataByFieldName(
                metadataFields ?? [],
                item.record.metadata,
                DESCRIPTION_FIELD,
              )}
              planName={
                item.record.plan
                  ? (planNameById.get(item.record.plan) ?? null)
                  : null
              }
              onClick={onOpenRecord}
              isActive={item.record.id === activeRecordId}
            />
          ) : (
            <TombstoneRow />
          )}
        </ItemCell>
      ))}
    </div>
  );
}
